import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Inbox } from "lucide-react";
import { Sidebar } from "@/components/chat/Sidebar";
import { ChatHeader } from "@/components/chat/ChatHeader";
import { MessageList } from "@/components/chat/MessageList";
import { ChatInput } from "@/components/chat/ChatInput";
import { ContextRail } from "@/components/chat/ContextRail";
import { StaffPanel } from "@/components/chat/StaffPanel";
import { CustomerHelp } from "@/components/chat/CustomerHelp";
import { CommandsSheet } from "@/components/chat/CommandsSheet";
import { fetchSessions, fetchMessages, fetchContext, deleteSession, streamChat, synthesizeSpeech, staffMe, getStaffToken, getStaffOverview } from "@/lib/api";
import { useHandsFree } from "@/hooks/useHandsFree";
import { enableReminders, getEod } from "@/lib/push";
import { useSpeechRecognition, speechSupported } from "@/hooks/useSpeechRecognition";

const LAST_SESSION_KEY = "apex_last_session";

const uuidv4 = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

const CARD_TOAST = {
  warranty: "Warranty claim logged",
  ticket: "Support ticket created",
  booking: "Appointment booked",
  lead: "Sales lead captured",
  diagnostic: "Diagnostic playbook generated",
};

export default function ChatApp() {
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(LAST_SESSION_KEY) || uuidv4());
  const [messages, setMessages] = useState([]);
  const [records, setRecords] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [mode, setMode] = useState("customer");
  const [model, setModel] = useState("claude-sonnet-4-6");
  const [railOpen, setRailOpen] = useState(false);
  const [staffOpen, setStaffOpen] = useState(false);
  const [jobsSearch, setJobsSearch] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [staff, setStaff] = useState(null);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [voice, setVoice] = useState("alloy");
  const [speaking, setSpeaking] = useState(false);
  const [liveMode, setLiveMode] = useState(false);
  const [wakeWord, setWakeWord] = useState(false);
  const [liveTranscript, setLiveTranscript] = useState("");
  const [spokenText, setSpokenText] = useState("");   // what Apex is currently saying (for barge-in echo filtering)
  const srSupported = speechSupported();

  const audioRef = useRef(null);
  const voiceRef = useRef(true);
  const voiceNameRef = useRef("alloy");
  const streamingRef = useRef(false);
  const speakingRef = useRef(false);
  const lastSpokeAtRef = useRef(0);          // timestamp (ms) when Apex's TTS last finished
  const bargeInAtRef = useRef(0);            // timestamp (ms) of the last barge-in interruption
  const lastVoiceRef = useRef({ text: "", ts: 0 });
  useEffect(() => { voiceRef.current = voiceEnabled; }, [voiceEnabled]);
  useEffect(() => { voiceNameRef.current = voice; }, [voice]);
  useEffect(() => { streamingRef.current = streaming; }, [streaming]);
  useEffect(() => {
    speakingRef.current = speaking;
    if (!speaking) lastSpokeAtRef.current = Date.now();
  }, [speaking]);

  const stopAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setSpeaking(false);
    setSpokenText("");
  }, []);

  const speak = useCallback(async (text) => {
    if (!text || !text.trim()) return;
    stopAudio();
    setSpeaking(true); // pause the mic immediately, before the TTS network call, to avoid echo
    setSpokenText(text);
    try {
      const blob = await synthesizeSpeech(text, voiceNameRef.current);
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audioRef.current = audio;
      const done = () => {
        URL.revokeObjectURL(url);
        // keep the mic paused for a short tail so speaker reverb isn't captured
        setTimeout(() => { if (audioRef.current === audio) { setSpeaking(false); setSpokenText(""); } }, 600);
      };
      audio.onended = done;
      audio.onerror = done;
      await audio.play();
    } catch (e) {
      setSpeaking(false);
      setSpokenText("");
    }
  }, [stopAudio]);

  // Barge-in: the user started talking while Apex was speaking — stop playback instantly so their words win.
  const handleBargeIn = useCallback(() => {
    bargeInAtRef.current = Date.now();
    stopAudio();
  }, [stopAudio]);

  const toggleVoice = useCallback((updater) => {
    setVoiceEnabled((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      if (!next) stopAudio();
      return next;
    });
  }, [stopAudio]);

  // Auto-greet a staff member by name the moment they sign in + surface what's outstanding.
  const greetStaff = useCallback(async (s) => {
    if (!s) return;
    const raw = (s.name || s.email || "").trim();
    const first = (raw.split(" ")[0] || raw).split("@")[0];
    const firstCap = first ? first.charAt(0).toUpperCase() + first.slice(1) : "there";
    let text = `Hi ${firstCap}, welcome back — you're signed in to it IN A bit.`;
    try {
      const ov = await getStaffOverview();
      const items = ov.items || [];
      if (items.length === 0) {
        text += ` Nothing's outstanding right now — all clear. Ask me about any customer's warranties, tickets, bookings or what's outstanding, and I can pull the details.`;
      } else {
        const c = ov.counts || {};
        const parts = [];
        if (c.warranty) parts.push(`${c.warranty} warranty${c.warranty > 1 ? " claims" : " claim"}`);
        if (c.ticket) parts.push(`${c.ticket} open ticket${c.ticket > 1 ? "s" : ""}`);
        if (c.booking) parts.push(`${c.booking} upcoming booking${c.booking > 1 ? "s" : ""}`);
        if (c.lead) parts.push(`${c.lead} sales lead${c.lead > 1 ? "s" : ""}`);
        if (c.bill) parts.push(`${c.bill} outstanding bill${c.bill > 1 ? "s" : ""}`);
        const headline = parts.length ? `You've got ${parts.join(", ").replace(/, ([^,]*)$/, " and $1")} outstanding.` : "";
        const lines = items.slice(0, 6).map((it) => {
          const label = it.type.charAt(0).toUpperCase() + it.type.slice(1);
          const who = it.customer ? ` — ${it.customer}` : "";
          const sum = it.summary ? `: ${it.summary}` : "";
          const st = it.status ? ` (${it.status})` : "";
          return `- **${label} ${it.ref}**${who}${sum}${st} · logged ${it.date}`;
        });
        text += ` ${headline}\n\n${lines.join("\n")}\n\nAsk me about any of these and I'll pull the full details.`;
      }
    } catch {
      text += ` Ask me about any customer's warranties, tickets, bookings or what's outstanding, and I can pull the details.`;
    }
    setMessages((prev) => [...prev, { id: uuidv4(), role: "assistant", content: text, streaming: false, card: null }]);
    if (voiceRef.current) speak(text);
  }, [speak]);

  const loadSessions = useCallback(() => { fetchSessions().then(setSessions).catch(() => {}); }, []);
  useEffect(() => { loadSessions(); }, [loadSessions]);

  // Restore signed-in staff identity so Apex knows who's chatting.
  useEffect(() => {
    if (getStaffToken()) staffMe().then(setStaff).catch(() => setStaff(null));
  }, []);

  // Tech Co-Pilot is staff-only — drop back to customer mode on sign-out.
  useEffect(() => {
    if (!staff && mode === "technician") setMode("customer");
  }, [staff, mode]);

  const openSession = useCallback(async (id) => {
    setSessionId(id);
    const [msgs, ctx] = await Promise.all([fetchMessages(id), fetchContext(id)]);
    setMessages(msgs);
    setRecords(ctx);
  }, []);

  // Device memory: resume the last conversation used on this device (once, on mount).
  const didResume = useRef(false);
  useEffect(() => {
    if (didResume.current) return;
    didResume.current = true;
    const last = localStorage.getItem(LAST_SESSION_KEY);
    if (last) openSession(last).catch(() => {});
  }, [openSession]);
  useEffect(() => { localStorage.setItem(LAST_SESSION_KEY, sessionId); }, [sessionId]);

  const newChat = useCallback(() => {
    setSessionId(uuidv4());
    setMessages([]);
    setRecords([]);
    setInput("");
  }, []);

  const removeSession = useCallback(async (id) => {
    await deleteSession(id);
    toast.success("Conversation deleted");
    if (id === sessionId) newChat();
    loadSessions();
  }, [sessionId, newChat, loadSessions]);

  const send = useCallback(async (overrideText, images = []) => {
    const text = (overrideText ?? input).trim();
    if ((!text && images.length === 0) || streaming) return;
    setInput("");
    setStreaming(true);
    const refHit = staff?.is_tech && text.match(/\b(TKT|BKG|WRN)-\d{3,6}\b/i);
    if (refHit) { setJobsSearch({ q: refHit[0].toUpperCase(), n: Date.now() }); setStaffOpen(true); }

    const userMsg = { id: uuidv4(), role: "user", content: text, images };
    const assistantMsg = { id: uuidv4(), role: "assistant", content: "", streaming: true, card: null };
    setMessages((prev) => [...prev, userMsg, assistantMsg]);
    const aId = assistantMsg.id;
    let acc = "";

    await streamChat(
      { session_id: sessionId, message: text, mode, model, images },
      {
        onDelta: (chunk) => {
          acc += chunk;
          setMessages((prev) => prev.map((m) => (m.id === aId ? { ...m, content: m.content + chunk } : m)));
        },
        onCard: (card) => {
          setMessages((prev) => prev.map((m) => (m.id === aId ? { ...m, card } : m)));
          if (card.type === "invoice_pdf") return;
          if (card.type === "booking_unavailable") {
            toast.warning("That time is taken", { description: "Nothing booked yet. Apex has offered the next free slot." });
            return;
          }
          if (card.type === "job") {
            if (card.ok) toast.success(`Job ${card.action} in timekeeper`, { description: card.data?.customer_name });
            else toast.error("Timekeeper update failed", { description: card.reason });
            return;
          }
          setRecords((prev) => [card, ...prev]);
          toast.success(CARD_TOAST[card.type] || "Record created", { description: card.ref });
        },
        onDone: () => {
          setMessages((prev) => prev.map((m) => (m.id === aId ? { ...m, streaming: false } : m)));
          setStreaming(false);
          loadSessions();
          if (voiceRef.current && acc.trim()) speak(acc);
        },
        onError: (detail) => {
          setMessages((prev) => prev.map((m) => (m.id === aId ? { ...m, streaming: false, content: m.content || "⚠️ Something went wrong. Please try again." } : m)));
          setStreaming(false);
          toast.error("Chat error", { description: String(detail).slice(0, 120) });
        },
      }
    );
  }, [input, streaming, sessionId, mode, model, loadSessions, speak, staff?.is_tech]);

  const onQuick = (prompt) => send(prompt);

  // Voice path only: drop duplicates and anything captured while Apex is talking/streaming
  // (stops the self-echo loop where Apex transcribes its own TTS and replies to itself).
  const handleVoiceFinal = useCallback((t) => {
    const text = (t || "").trim();
    if (!text) return;
    const now = Date.now();
    if (streamingRef.current || speakingRef.current) return;      // Apex is busy
    // ignore the TTS echo tail, unless the user just barged in (then their words should go through)
    if (now - lastSpokeAtRef.current < 1200 && now - bargeInAtRef.current > 1500) return;
    const last = lastVoiceRef.current;
    if (text === last.text && now - last.ts < 8000) return;       // ignore repeated identical utterance
    lastVoiceRef.current = { text, ts: now };
    send(text);
  }, [send]);

  const toggleLive = useCallback((force) => {
    setLiveMode((prev) => {
      const next = typeof force === "boolean" ? force : !prev;
      if (next && !prev) { setVoiceEnabled(true); toast.success("Live voice on — just start talking"); }
      else if (!next && prev) { stopAudio(); toast.info("Live voice off"); }
      return next;
    });
  }, [stopAudio]);

  const toggleWakeWord = useCallback(() => {
    if (!srSupported) { toast.error("Wake word isn't supported on this browser"); return; }
    setWakeWord((prev) => {
      const next = !prev;
      toast[next ? "success" : "info"](next ? "Wake word on — say \u201cHey Apex\u201d" : "Wake word off");
      return next;
    });
  }, [srSupported]);

  // Speech recognition drives wake-word + live transcript (when the browser supports it).
  // In live mode we keep listening DURING Apex's speech so the user can barge in mid-sentence.
  let srMode = "off";
  if (srSupported && !streaming) {
    if (liveMode) srMode = "live";          // stays live even while speaking (barge-in)
    else if (wakeWord && !speaking) srMode = "wake";
  }
  const { listening: srListening, interim: srInterim } = useSpeechRecognition({
    mode: srMode,
    speaking,
    spokenText,
    onBargeIn: handleBargeIn,
    onWake: (rest) => { toggleLive(true); if (rest) send(rest); },
    onFinal: handleVoiceFinal,
    onError: (e) => {
      if (e && (e.error === "not-allowed" || e.error === "service-not-allowed")) {
        toast.error("Microphone permission needed for voice");
        setWakeWord(false);
        setLiveMode(false);
      }
    },
  });
  useEffect(() => { setLiveTranscript(srMode === "live" ? srInterim : ""); }, [srInterim, srMode]);

  // Opened from a reminder notification: /?prompt=log%20my%20open%20jobs
  const promptHandled = useRef(false);
  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get("prompt");
    if (!p || !staff || promptHandled.current) return;
    promptHandled.current = true;
    window.history.replaceState({}, "", window.location.pathname);
    send(p);
  }, [staff, send]);

  // After 5pm, nudge the tech about today's jobs still open with no time logged.
  useEffect(() => {
    if (!staff?.is_tech) return;
    getEod().then((r) => {
      if (!r.after_cutoff || !r.pending?.length) return;
      const names = r.pending.slice(0, 3).map((j) => j.customer_name).join(", ");
      toast.warning(`${r.pending.length} job${r.pending.length > 1 ? "s" : ""} still open with no time logged`, {
        description: names,
        duration: 15000,
        action: { label: "Log now", onClick: () => send("Help me log time for my open jobs today") },
      });
    }).catch(() => {});
  }, [staff?.is_tech]); // eslint-disable-line react-hooks/exhaustive-deps

  // Morning briefing: first open of the day before 12pm (tech only).
  const [commandsOpen, setCommandsOpen] = useState(false);
  useEffect(() => {
    if (!staff?.is_tech) return;
    const now = new Date();
    const today = now.toLocaleDateString("en-CA");
    if (now.getHours() >= 12 || localStorage.getItem("apex_brief_date") === today) return;
    if (new URLSearchParams(window.location.search).get("prompt")) return;
    localStorage.setItem("apex_brief_date", today);
    const t = setTimeout(() => send("Brief me"), 1200);
    return () => clearTimeout(t);
  }, [staff?.is_tech]); // eslint-disable-line react-hooks/exhaustive-deps

  const turnOnReminders = useCallback(async () => {
    try {
      await enableReminders();
      toast.success("Notifications are on for this device: new jobs + 5pm reminders", { description: "Check for a test notification." });
    } catch (e) {
      toast.error("Couldn't turn on reminders", { description: e?.response?.data?.detail || e.message });
    }
  }, []);

  // Fallback: Whisper-based hands-free only when Web Speech API is unavailable.
  // Keep listening during Apex's speech too so the user can barge in (getUserMedia echo-cancellation
  // keeps self-echo low); onSpeechStart stops playback the moment the user starts talking.
  const hfActive = !srSupported && liveMode && !streaming;
  const { listening: hfListening, transcribing: hfTranscribing } = useHandsFree({
    active: hfActive,
    onUtterance: handleVoiceFinal,
    onSpeechStart: () => { if (speakingRef.current) handleBargeIn(); },
    onError: () => {},
  });

  const listening = srSupported ? srListening : hfListening;
  const transcribing = srSupported ? false : hfTranscribing;

  return (
    <div className="command-bg ios-full-height h-screen w-screen flex overflow-hidden text-[var(--text-primary)]">
      <Sidebar
        sessions={sessions}
        currentId={sessionId}
        mode={mode}
        onNew={newChat}
        onSelect={openSession}
        onDelete={removeSession}
        onQuick={onQuick}
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <main className="flex-1 flex flex-col min-w-0">
        <ChatHeader
          mode={mode} setMode={setMode} model={model} setModel={setModel}
          voiceEnabled={voiceEnabled} setVoiceEnabled={toggleVoice} speaking={speaking}
          voice={voice} setVoice={setVoice}
          wakeWord={wakeWord} onToggleWakeWord={toggleWakeWord} wakeSupported={srSupported}
          staff={staff}
          onOpenStaff={() => setStaffOpen(true)}
          onOpenSidebar={() => setSidebarOpen(true)}
          onOpenHelp={() => setHelpOpen(true)}
          onEnableReminders={turnOnReminders}
          onOpenCommands={() => setCommandsOpen(true)}
        />
        <MessageList messages={messages} streaming={streaming} mode={mode} onSpeak={speak} onPickSlot={(t) => send(t)} />
        <ChatInput
          value={input} onChange={setInput} onSend={(imgs) => send(undefined, imgs)} disabled={streaming} mode={mode}
          liveMode={liveMode} onToggleLive={toggleLive}
          listening={listening} transcribing={transcribing} speaking={speaking}
          liveTranscript={liveTranscript} wakeWord={wakeWord}
        />
      </main>

      {/* Mobile records button */}
      {records.length > 0 && (
        <button
          data-testid="open-records-button"
          onClick={() => setRailOpen(true)}
          className="xl:hidden fixed bottom-28 right-4 z-30 h-12 w-12 rounded-full bg-[var(--surface)] border border-[var(--border)] grid place-items-center shadow-lg"
        >
          <Inbox size={18} className="text-[var(--primary)]" />
          <span className="absolute -top-1 -right-1 h-5 min-w-5 px-1 rounded-full bg-[var(--primary)] text-black text-[10px] font-bold grid place-items-center">{records.length}</span>
        </button>
      )}

      <ContextRail records={records} open={railOpen} onClose={() => setRailOpen(false)} />
      <CommandsSheet open={commandsOpen} onOpenChange={setCommandsOpen} />
      <StaffPanel open={staffOpen} jobsSearch={jobsSearch} onClose={() => setStaffOpen(false)} onStaffChange={setStaff} onStaffLogin={greetStaff} />
      <CustomerHelp open={helpOpen} onClose={() => setHelpOpen(false)} onAsk={(t) => send(t)} />
    </div>
  );
}
